import React, { useEffect, useMemo, useState } from 'react';
import { getDocs, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { cycleDoc, heartbeatDoc, usersColl } from '../db';
import { queueNotification } from '../hooks/usePushNotification';
import { Screen, UserProfile } from '../App';
import {
  addDays,
  CycleProfile,
  CycleState,
  dateKeyFromDate,
  defaultCycleProfile,
  diffDays,
  formatCycleDate,
  getCycleInfo,
  parseDateKey,
  todayKey,
} from '../utils/cycle';
import { canUseCycleFeature, inferGender, isFemaleProfile } from '../utils/gender';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

const SYMPTOMS = ['Kramp', 'Baş ağrısı', 'Hassasiyet', 'Yorgunluk', 'Şişkinlik', 'İştah', 'Duygusal', 'Bel ağrısı'];
const ENERGY = ['Sakin', 'Normal', 'Hassas', 'Sosyal', 'Dinlenme'];
const FLOW_OPTIONS: { id: CycleProfile['flow']; label: string }[] = [
  { id: 'none', label: 'Yok' },
  { id: 'light', label: 'Hafif' },
  { id: 'medium', label: 'Orta' },
  { id: 'heavy', label: 'Yoğun' },
];

function fallbackPartner(name: string) {
  return name === 'Serhat' ? 'Ayça' : 'Serhat';
}

function timeAgo(ts: any) {
  const d = ts?.toDate?.();
  if (!d) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diff < 1) return 'az önce';
  if (diff < 60) return `${diff} dk önce`;
  if (diff < 1440) return `${Math.floor(diff / 60)} saat önce`;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

function cyclePhaseForDate(profile: CycleProfile, key: string) {
  const start = parseDateKey(profile.lastPeriodStart);
  const date = parseDateKey(key);
  const diff = diffDays(start, date);
  if (diff < 0) return 'none';
  const cycleLength = Math.min(45, Math.max(21, Number(profile.cycleLength) || 28));
  const periodLength = Math.min(10, Math.max(2, Number(profile.periodLength) || 5));
  const day = (diff % cycleLength) + 1;
  const ovulationDay = Math.max(periodLength + 3, cycleLength - 14);
  const fertileStart = Math.max(periodLength + 1, ovulationDay - 5);
  const fertileEnd = Math.min(cycleLength, ovulationDay + 1);
  if (day <= periodLength) return 'period';
  if (day >= fertileStart && day <= fertileEnd) return day === ovulationDay ? 'ovulation' : 'fertile';
  if (day >= cycleLength - 5) return 'pms';
  return 'regular';
}

function CycleCalendar({ profile }: { profile: CycleProfile }) {
  const today = parseDateKey(todayKey());
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = addDays(first, -startOffset);
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const gridLength = startOffset + daysInMonth > 35 ? 42 : 35;
  const days = Array.from({ length: gridLength }, (_, index) => {
    const date = addDays(gridStart, index);
    const key = dateKeyFromDate(date);
    return {
      key,
      day: date.getDate(),
      inMonth: date.getMonth() === today.getMonth(),
      today: key === todayKey(),
      phase: cyclePhaseForDate(profile, key),
    };
  });

  return (
    <article className="cycle-calendar-card">
      <div className="cycle-card-top">
        <div>
          <small>Takvim</small>
          <h3>{today.toLocaleDateString('tr-TR', { month: 'long' })}</h3>
        </div>
        <b>{profile.ownerName}</b>
      </div>

      <div className="cycle-weekdays">
        {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(day => <span key={day}>{day}</span>)}
      </div>

      <div className="cycle-calendar-grid">
        {days.map(day => (
          <span key={day.key} className={`${day.phase} ${day.inMonth ? '' : 'muted'} ${day.today ? 'today' : ''}`}>
            {day.day}
          </span>
        ))}
      </div>

      <div className="cycle-legend">
        <span className="period">Menstr.</span>
        <span className="fertile">Ovulasyon</span>
        <span className="pms">Premenstr.</span>
      </div>
    </article>
  );
}

export default function CycleTracker({ user, navigate, backTo = 'us' }: Props) {
  const [state, setState] = useState<CycleState>({});
  const [members, setMembers] = useState<UserProfile[]>([user]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CycleProfile>(() => defaultCycleProfile(user.uid, user.displayName));
  const [saving, setSaving] = useState(false);
  const [supportingUid, setSupportingUid] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    return onSnapshot(cycleDoc(user.coupleId), snap => {
      setState(snap.exists() ? snap.data() as CycleState : {});
    });
  }, [user.coupleId]);

  useEffect(() => {
    getDocs(usersColl()).then(snap => {
      const sameCouple = snap.docs
        .map(d => ({ uid: d.id, ...d.data() } as UserProfile))
        .filter(member => member.coupleId === user.coupleId);
      setMembers(sameCouple.length ? sameCouple : [user]);
    }).catch(() => setMembers([user]));
  }, [user.coupleId, user.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const profiles = state.profiles || {};
  const myProfile = profiles[user.uid];
  const memberMap = useMemo(() => new Map(members.map(member => [member.uid, member])), [members]);
  const featureEnabled = canUseCycleFeature(members.length ? members : [user]);
  const myGender = inferGender(memberMap.get(user.uid) || user);
  const canEditMine = isFemaleProfile(memberMap.get(user.uid) || user);
  const partnerProfiles = Object.values(profiles).filter(p =>
    p.ownerUid !== user.uid &&
    p.enabled &&
    isFemaleProfile(memberMap.get(p.ownerUid) || { displayName: p.ownerName })
  );
  const displayProfiles = [
    ...(myProfile?.enabled && canEditMine ? [myProfile] : []),
    ...partnerProfiles,
  ];
  const featured = displayProfiles[0];
  const featuredInfo = getCycleInfo(featured);

  const dashboardText = useMemo(() => {
    if (!featured) {
      return {
        eyebrow: 'Çift taraflı takip',
        title: 'Regl döngüsü burada sakin ve anlaşılır dursun.',
        body: 'Takvim, fazlar, belirtiler ve destek tek yerde.',
      };
    }
    return {
      eyebrow: featured.ownerUid === user.uid ? 'Senin döngün' : `${featured.ownerName} için destek`,
      title: featuredInfo.title,
      body: featuredInfo.detail,
    };
  }, [featured, featuredInfo.detail, featuredInfo.title, user.uid]);

  const openEditor = () => {
    if (!canEditMine) {
      setStatus('Döngü sadece kadın profili için düzenlenir.');
      return;
    }
    setDraft({ ...defaultCycleProfile(user.uid, user.displayName), ...(myProfile || {}) });
    setEditing(true);
    setStatus('');
  };

  const updateDraft = <K extends keyof CycleProfile>(key: K, value: CycleProfile[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const toggleSymptom = (item: string) => {
    setDraft(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(item)
        ? prev.symptoms.filter(s => s !== item)
        : [...prev.symptoms, item],
    }));
  };

  const save = async () => {
    if (!canEditMine) return;
    setSaving(true);
    const next: CycleProfile = {
      ...draft,
      ownerUid: user.uid,
      ownerName: user.displayName,
      ownerGender: 'female',
      enabled: true,
      cycleLength: Math.min(45, Math.max(21, Number(draft.cycleLength) || 28)),
      periodLength: Math.min(10, Math.max(2, Number(draft.periodLength) || 5)),
      updatedAt: serverTimestamp(),
    };
    await setDoc(cycleDoc(user.coupleId), { profiles: { [user.uid]: next } }, { merge: true });
    setSaving(false);
    setEditing(false);
    setStatus('Döngü kaydedildi');
  };

  const markTodayStarted = async () => {
    if (!canEditMine) {
      setStatus('Döngü sadece kadın profili için düzenlenir.');
      return;
    }
    const next = {
      ...defaultCycleProfile(user.uid, user.displayName),
      ...(myProfile || {}),
      ownerUid: user.uid,
      ownerName: user.displayName,
      ownerGender: 'female',
      enabled: true,
      lastPeriodStart: todayKey(),
      flow: myProfile?.flow || 'medium',
      updatedAt: serverTimestamp(),
    };
    await setDoc(cycleDoc(user.coupleId), { profiles: { [user.uid]: next } }, { merge: true });
    setStatus('Bugün başlangıç olarak kaydedildi');
  };

  const disableTracking = async () => {
    if (!canEditMine) return;
    await setDoc(cycleDoc(user.coupleId), {
      profiles: {
        [user.uid]: {
          ...(myProfile || defaultCycleProfile(user.uid, user.displayName)),
          enabled: false,
          updatedAt: serverTimestamp(),
        },
      },
    }, { merge: true });
    setEditing(false);
    setStatus('Döngü takibi kapatıldı');
  };

  const sendSupport = async (target: CycleProfile) => {
    if (target.ownerUid === user.uid || supportingUid) return;
    setSupportingUid(target.ownerUid);
    const info = getCycleInfo(target);
    const message = info.phase === 'period'
      ? 'Bugün yanında daha yumuşak olacağım.'
        : info.phase === 'pms'
          ? 'Bu günlerde planları sakin tutalım.'
          : 'Döngünü aklımda tutuyorum.';

    try {
      await setDoc(cycleDoc(user.coupleId), {
        support: {
          [target.ownerUid]: {
            fromUid: user.uid,
            fromName: user.displayName,
            message,
            createdAt: serverTimestamp(),
          },
        },
      }, { merge: true });

      await setDoc(heartbeatDoc(user.coupleId, target.ownerUid), {
        fromName: user.displayName,
        fromUid: user.uid,
        timestamp: serverTimestamp(),
        unread: true,
        kind: 'cycle_support',
      }, { merge: true });

      queueNotification(
        user.coupleId,
        target.ownerUid,
        `${user.displayName} döngünü aklında tuttu`,
        message,
        'cycle-support'
      ).catch(() => {});

      setStatus(`${target.ownerName} için destek işareti gönderildi`);
    } catch {
      setStatus('Destek gönderilemedi. Birazdan tekrar deneyin.');
    } finally {
      setSupportingUid(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Döngü Takibi</h1>
      </div>

      <div className="cycle-page">
        {!featureEnabled ? (
          <section className="cycle-hero-card">
            <div>
              <p className="eyebrow">Döngü kapalı</p>
              <h2>Bu çift profili için regl döngüsü gösterilmiyor.</h2>
              <span>İki erkek profilde bu alan saklanır.</span>
            </div>
          </section>
        ) : (
          <>
        <section className="cycle-hero-card">
          <div>
            <p className="eyebrow">{dashboardText.eyebrow}</p>
            <h2>{dashboardText.title}</h2>
            <span>{dashboardText.body}</span>
          </div>
          <div
            className="cycle-orb"
            style={{
              '--cycle-progress': `${featuredInfo.progress}%`,
              '--cycle-accent': featuredInfo.accent,
            } as React.CSSProperties}
          >
            <strong>{featuredInfo.active ? featuredInfo.day : '—'}</strong>
            <small>{featuredInfo.active ? `${featuredInfo.cycleLength} gün` : 'kur'}</small>
          </div>
        </section>

        <section className="cycle-actions">
          {canEditMine ? (
            <>
              <button onClick={markTodayStarted}>Bugün başladı</button>
              <button onClick={openEditor}>{myProfile?.enabled ? 'Döngümü düzenle' : 'Döngümü ekle'}</button>
            </>
          ) : (
            <button className="wide" onClick={() => myGender ? undefined : navigate('settings')}>
              {myGender === 'male' ? 'Partner ekleyince görünür' : 'Profil cinsiyetini seç'}
            </button>
          )}
        </section>

        {status && <div className="cycle-status">{status}</div>}

        <section className="cycle-grid">
          {featured && <CycleCalendar profile={featured} />}

          {displayProfiles.map(profile => {
            const info = getCycleInfo(profile);
            const isMine = profile.ownerUid === user.uid;
            const support = state.support?.[profile.ownerUid];
            return (
              <article key={profile.ownerUid} className="cycle-profile-card">
                <div className="cycle-card-top">
                  <div>
                    <small>{isMine ? 'Benim döngüm' : `${profile.ownerName} için takip`}</small>
                    <h3>{profile.ownerName}</h3>
                  </div>
                  <b style={{ color: info.accent }}>{info.title}</b>
                </div>

                <div className="cycle-mini-progress">
                  <span style={{ width: `${info.progress}%`, background: info.accent }} />
                </div>

                <div className="cycle-metrics">
                  <div>
                    <strong>{info.day || '—'}</strong>
                    <small>döngü günü</small>
                  </div>
                  <div>
                    <strong>{info.daysToNext}</strong>
                    <small>gün kaldı</small>
                  </div>
                  <div>
                    <strong>{formatCycleDate(info.nextStartKey)}</strong>
                    <small>sonraki tahmin</small>
                  </div>
                </div>

                <p>{info.supportHint}</p>

                <div className="cycle-tags">
                  {profile.flow !== 'none' && <span>Akış: {FLOW_OPTIONS.find(f => f.id === profile.flow)?.label}</span>}
                  {profile.energy && <span>{profile.energy}</span>}
                  {profile.symptoms.slice(0, 4).map(symptom => <span key={symptom}>{symptom}</span>)}
                </div>

                {profile.note && <em>{profile.note}</em>}

                {support && (
                  <div className="cycle-support-note">
                    <strong>{support.fromName}</strong>
                    <span>{support.message}</span>
                    <small>{timeAgo(support.createdAt)}</small>
                  </div>
                )}

                <div className="cycle-card-actions">
                  {isMine ? (
                    <button onClick={openEditor}>Düzenle</button>
                  ) : (
                    <button onClick={() => sendSupport(profile)} disabled={supportingUid === profile.ownerUid}>
                      {supportingUid === profile.ownerUid ? 'Gönderiliyor...' : 'Destek gönder'}
                    </button>
                  )}
                </div>
              </article>
            );
          })}

          {!displayProfiles.length && (
            <div className="empty-state">
              Henüz döngü eklenmedi.
            </div>
          )}

          {!partnerProfiles.length && (
            <article className="cycle-profile-card muted">
              <div className="cycle-card-top">
                <div>
                  <small>Çift taraflı alan</small>
                  <h3>{fallbackPartner(user.displayName)}</h3>
                </div>
                <b>Bekliyor</b>
              </div>
              <p>Partner eklediğinde burada görünür.</p>
            </article>
          )}
        </section>

        <section className="cycle-disclaimer">
          <strong>Not</strong>
          <p>Tahmin ve destek içindir; tıbbi karar yerine geçmez.</p>
        </section>
          </>
        )}
      </div>

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(false)}>
          <div className="modal-sheet cycle-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2>Döngümü düzenle</h2>
            <p className="modal-sub">Kendi döngünü sen güncellersin.</p>

            <label className="cycle-field">
              Son regl başlangıcı
              <input type="date" value={draft.lastPeriodStart} onChange={e => updateDraft('lastPeriodStart', e.target.value)} />
            </label>

            <div className="cycle-two-fields">
              <label className="cycle-field">
                Döngü
                <input type="number" min={21} max={45} value={draft.cycleLength}
                  onChange={e => updateDraft('cycleLength', Number(e.target.value))} />
              </label>
              <label className="cycle-field">
                Regl
                <input type="number" min={2} max={10} value={draft.periodLength}
                  onChange={e => updateDraft('periodLength', Number(e.target.value))} />
              </label>
            </div>

            <label className="cycle-field">
              Akış
              <select value={draft.flow} onChange={e => updateDraft('flow', e.target.value as CycleProfile['flow'])}>
                {FLOW_OPTIONS.map(flow => <option key={flow.id} value={flow.id}>{flow.label}</option>)}
              </select>
            </label>

            <div className="cycle-field">
              Enerji
              <div className="cycle-chip-row">
                {ENERGY.map(item => (
                  <button key={item} className={draft.energy === item ? 'active' : ''} onClick={() => updateDraft('energy', item)}>{item}</button>
                ))}
              </div>
            </div>

            <div className="cycle-field">
              Belirtiler
              <div className="cycle-chip-row">
                {SYMPTOMS.map(item => (
                  <button key={item} className={draft.symptoms.includes(item) ? 'active' : ''} onClick={() => toggleSymptom(item)}>{item}</button>
                ))}
              </div>
            </div>

            <textarea
              placeholder="Bugün bilinmesini istediğin kısa not"
              value={draft.note}
              onChange={e => updateDraft('note', e.target.value)}
              maxLength={140}
            />

            <div className="split-actions">
              {myProfile?.enabled ? <button className="feature-secondary" onClick={disableTracking}>Kapat</button> : <button className="feature-secondary" onClick={() => setEditing(false)}>Vazgeç</button>}
              <button className="feature-primary" onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
};
